import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Disc, Sparkles, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '../utils/api';

export default function WaitlistPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    quantity: '1'
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/waitlist`, formData);
      setSubmitted(true);
      toast.success('You\'re on the waitlist!');
    } catch (error) {
      toast.error('Failed to join waitlist. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen py-32 bg-[#1A1412]" data-testid="waitlist-success">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, type: "spring" }}
          >
            <CheckCircle className="w-24 h-24 text-[#CFB53B] mx-auto mb-6" />
          </motion.div>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl font-bold text-[#FDFBF7] mb-4">
            You're On The List!
          </h1>
          <p className="text-[#A89B95] text-lg font-['Outfit'] mb-8">
            Thank you for your interest in the Limited Edition Vintage Ave Record Label Sampler. 
            We'll notify you when pre-orders open for Fall 2026!
          </p>
          <a href="/shop">
            <Button className="bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full px-8 py-3">
              Continue Shopping
            </Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-32 bg-[#1A1412]" data-testid="waitlist-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="text-[#CFB53B]" size={24} />
            <span className="text-[#CFB53B] uppercase tracking-widest text-sm font-['Outfit']">Limited Edition</span>
            <Sparkles className="text-[#CFB53B]" size={24} />
          </div>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-[#FDFBF7] mb-4 tracking-tight">
            Pre-Order Waitlist
          </h1>
          <p className="text-[#A89B95] text-lg font-['Outfit'] max-w-2xl mx-auto">
            Be first in line for the Vintage Ave Record Label Sampler - Only 500 pressed!
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Vinyl Preview */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex justify-center"
          >
            <div className="relative">
              {/* Spinning Vinyl */}
              <div className="w-64 h-64 rounded-full bg-gradient-to-br from-[#1A1412] to-[#2C221F] border-4 border-[#D97736] flex items-center justify-center relative animate-spin shadow-2xl" style={{ animationDuration: '6s' }}>
                <div className="absolute inset-4 rounded-full bg-gradient-to-br from-[#D97736] to-[#CFB53B] opacity-20"></div>
                <div className="w-16 h-16 rounded-full bg-[#CFB53B] flex items-center justify-center">
                  <div className="w-6 h-6 rounded-full bg-[#1A1412]"></div>
                </div>
                {/* Grooves */}
                <div className="absolute inset-8 rounded-full border border-[#3D302C]"></div>
                <div className="absolute inset-12 rounded-full border border-[#3D302C]"></div>
                <div className="absolute inset-16 rounded-full border border-[#3D302C]"></div>
                <div className="absolute inset-20 rounded-full border border-[#3D302C]"></div>
              </div>
              {/* Label */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
                <p className="text-[#1A1412] text-xs font-bold">VINTAGE AVE</p>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Card className="bg-[#2C221F] border-[#3D302C]">
              <CardContent className="p-8">
                <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#FDFBF7] mb-2">
                  Join The Waitlist
                </h2>
                <p className="text-[#A89B95] text-sm mb-6 font-['Outfit']">
                  Reserve your copy of this exclusive compilation. Release: Fall 2026
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <Label className="text-[#FDFBF7] mb-2 block">Full Name *</Label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="Your name"
                      className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7] placeholder:text-[#6B5B54]"
                    />
                  </div>

                  <div>
                    <Label className="text-[#FDFBF7] mb-2 block">Email Address *</Label>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7] placeholder:text-[#6B5B54]"
                    />
                  </div>

                  <div>
                    <Label className="text-[#FDFBF7] mb-2 block">Phone Number</Label>
                    <Input
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(555) 555-5555"
                      className="bg-[#1A1412] border-[#3D302C] text-[#FDFBF7] placeholder:text-[#6B5B54]"
                    />
                  </div>

                  <div>
                    <Label className="text-[#FDFBF7] mb-2 block">Quantity Interested</Label>
                    <select
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      className="w-full bg-[#1A1412] border border-[#3D302C] text-[#FDFBF7] rounded-md px-3 py-2"
                    >
                      <option value="1">1 Copy</option>
                      <option value="2">2 Copies</option>
                      <option value="3">3 Copies</option>
                      <option value="5">5+ Copies</option>
                    </select>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#D97736] hover:bg-[#F08F4D] text-white rounded-full py-6 text-lg"
                  >
                    {loading ? 'Joining...' : 'Join Waitlist'}
                  </Button>

                  <p className="text-[#6B5B54] text-xs text-center font-['Outfit']">
                    By joining, you'll receive updates about the release. No payment required now.
                  </p>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-center"
        >
          <div className="p-6">
            <Disc className="w-10 h-10 text-[#D97736] mx-auto mb-4" />
            <h3 className="text-[#FDFBF7] font-['Playfair_Display'] text-lg font-bold mb-2">Premium Vinyl</h3>
            <p className="text-[#A89B95] text-sm font-['Outfit']">180g heavyweight vinyl for superior sound quality</p>
          </div>
          <div className="p-6">
            <Sparkles className="w-10 h-10 text-[#CFB53B] mx-auto mb-4" />
            <h3 className="text-[#FDFBF7] font-['Playfair_Display'] text-lg font-bold mb-2">Limited Run</h3>
            <p className="text-[#A89B95] text-sm font-['Outfit']">Only 500 copies will ever be pressed</p>
          </div>
          <div className="p-6">
            <Disc className="w-10 h-10 text-[#D97736] mx-auto mb-4" />
            <h3 className="text-[#FDFBF7] font-['Playfair_Display'] text-lg font-bold mb-2">All Artists</h3>
            <p className="text-[#A89B95] text-sm font-['Outfit']">Featuring tracks from every roster artist</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
